/** Own-profile management: details, password and optional two-factor authentication. */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, FormField, Input, Textarea } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { authApi } from '@/services/api';
import { ApiError } from '@/services/client';

export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState({
    full_name: user?.full_name ?? '',
    job_title: user?.job_title ?? '',
    phone: user?.phone ?? '',
    bio: user?.bio ?? '',
  });
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [notice, setNotice] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [totp, setTotp] = useState<{ uri: string; secret: string } | null>(null);

  const profileMutation = useMutation({
    mutationFn: () => authApi.updateProfile(profile),
    onSuccess: async () => {
      setNotice('Profile updated.');
      setErrors({});
      await refreshUser();
    },
    onError: (error) => setErrors(error instanceof ApiError ? error.fields : { __all__: 'Update failed.' }),
  });

  const passwordMutation = useMutation({
    mutationFn: () => authApi.changePassword(passwords.current, passwords.next),
    onSuccess: async () => {
      setNotice('Password changed.');
      setErrors({});
      setPasswords({ current: '', next: '', confirm: '' });
      await refreshUser();
    },
    onError: (error) => setErrors(error instanceof ApiError ? error.fields : { __all__: 'Password change failed.' }),
  });

  const totpMutation = useMutation({
    mutationFn: () => authApi.startTwoFactor(),
    onSuccess: (data) => setTotp({ uri: data.provisioning_uri, secret: data.secret }),
  });

  if (!user) return null;

  const passwordsMatch = passwords.next === passwords.confirm;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Your profile</h1>
        <p className="mt-1 text-sm text-muted">
          Signed in as {user.email} · roles: {user.roles.join(', ').replace(/_/g, ' ')}
        </p>
      </div>

      {notice && <Alert tone="success">{notice}</Alert>}
      {errors.__all__ && <Alert tone="danger">{errors.__all__}</Alert>}
      {user.must_change_password && (
        <Alert tone="warning" title="You are using a temporary password">
          Set a permanent password below before continuing.
        </Alert>
      )}

      <Card>
        <h2 className="text-base font-semibold text-ink">Details</h2>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <FormField label="Full name" htmlFor="profile-name" required {...(errors.full_name ? { error: errors.full_name } : {})}>
            <Input
              id="profile-name"
              value={profile.full_name}
              onChange={(event) => setProfile({ ...profile, full_name: event.target.value })}
            />
          </FormField>
          <FormField label="Job title" htmlFor="profile-title">
            <Input
              id="profile-title"
              value={profile.job_title}
              onChange={(event) => setProfile({ ...profile, job_title: event.target.value })}
            />
          </FormField>
          <FormField label="Phone" htmlFor="profile-phone">
            <Input id="profile-phone" value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} />
          </FormField>
          <div className="sm:col-span-2">
            <FormField label="Author bio" htmlFor="profile-bio" hint="Shown on your byline when you publish an article.">
              <Textarea id="profile-bio" rows={3} value={profile.bio} onChange={(event) => setProfile({ ...profile, bio: event.target.value })} />
            </FormField>
          </div>
        </div>
        <Button className="mt-5" onClick={() => profileMutation.mutate()} loading={profileMutation.isPending}>
          Save details
        </Button>
      </Card>

      <Card>
        <h2 className="text-base font-semibold text-ink">Password</h2>
        <p className="mt-1 text-sm text-muted">At least 12 characters, and not a password you use elsewhere.</p>
        <div className="mt-5 space-y-5">
          <FormField
            label="Current password"
            htmlFor="password-current"
            required
            {...(errors.current_password ? { error: errors.current_password } : {})}
          >
            <Input
              id="password-current"
              type="password"
              autoComplete="current-password"
              value={passwords.current}
              onChange={(event) => setPasswords({ ...passwords, current: event.target.value })}
              error={Boolean(errors.current_password)}
            />
          </FormField>
          <FormField label="New password" htmlFor="password-new" required {...(errors.new_password ? { error: errors.new_password } : {})}>
            <Input
              id="password-new"
              type="password"
              autoComplete="new-password"
              value={passwords.next}
              onChange={(event) => setPasswords({ ...passwords, next: event.target.value })}
              error={Boolean(errors.new_password)}
            />
          </FormField>
          <FormField
            label="Confirm new password"
            htmlFor="password-confirm"
            required
            {...(!passwordsMatch && passwords.confirm ? { error: 'The two passwords do not match.' } : {})}
          >
            <Input
              id="password-confirm"
              type="password"
              autoComplete="new-password"
              value={passwords.confirm}
              onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })}
              error={!passwordsMatch && Boolean(passwords.confirm)}
            />
          </FormField>
        </div>
        <Button
          className="mt-5"
          onClick={() => passwordMutation.mutate()}
          disabled={!passwords.current || !passwords.next || !passwordsMatch}
          loading={passwordMutation.isPending}
        >
          Change password
        </Button>
      </Card>

      <Card>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-ink">Two-factor authentication</h2>
            <p className="mt-1 text-sm text-muted">
              An authenticator app adds a second factor to your sign-in. Recommended for anyone who can publish or manage
              users.
            </p>
          </div>
          <Badge tone={user.two_factor_enabled ? 'success' : 'neutral'}>{user.two_factor_enabled ? 'Enabled' : 'Not enabled'}</Badge>
        </div>

        {!user.two_factor_enabled && (
          <>
            <Button variant="secondary" className="mt-5" onClick={() => totpMutation.mutate()} loading={totpMutation.isPending}>
              Set up two-factor authentication
            </Button>

            {totp && (
              <div className="mt-5 rounded-md border border-line bg-raised p-4">
                <p className="text-sm text-ink">
                  Add this secret to your authenticator app, then confirm with a generated code.
                </p>
                <p className="mt-3 break-all rounded bg-surface px-3 py-2 font-mono text-xs">{totp.secret}</p>
                <p className="mt-2 break-all text-xs text-muted">{totp.uri}</p>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
